<template>
  <div v-if="hasAccessLevel" class="d-flex align-items-center h-100">
    <GuDialog v-model:open="isOpen">
      <GuDialogTrigger as-child>
        <GuButton
          size="sm"
          :variant="accessLevelFlag ? 'primary' : 'secondary'"
          outline
          :disabled="actionInProgress || !ruleId"
          :title="ruleId ? '' : 'Application access toggle is not configured for this row'"
        >
          {{ accessLevelFlag ? 'All' : 'Personal' }}
        </GuButton>
      </GuDialogTrigger>

      <GuDialogContent>
        <template #title>
          <h5 class="modal-title fs-18">Change Application Access</h5>
        </template>

        <p>
          Are you sure you want to change the application access level for
          <strong>{{ userName }}</strong>
          to <span class="text-danger">{{ accessLevelFlag ? 'Personal' : 'All' }}</span>?
        </p>

        <template #footer>
          <GuDialogClose as-child>
            <GuButton variant="secondary" :disabled="actionInProgress">Cancel</GuButton>
          </GuDialogClose>

          <GuButton
            :loading="actionInProgress"
            @click="confirmToggle"
          >
            Confirm
          </GuButton>
        </template>
      </GuDialogContent>
    </GuDialog>
  </div>

  <span v-else class="text-muted">None</span>
</template>

<script setup>
import { computed, ref, toRefs } from 'vue'

import { GuButton } from '@shared/gu-components/button'
import { GuDialog, GuDialogClose, GuDialogContent, GuDialogTrigger } from '@shared/gu-components/dialog'
import { useToast } from '@shared/gu-components/toast'

import { triggerRule } from '@customers/pge/api/triggerRule.js'
import { applyActionResponse } from '@customers/pge/api/ruleResponse.js'

const ACCESS_LEVELS = ['All', 'Personal']

const props = defineProps({
  params: {
    type: Object,
    default: () => ({}),
  },
})
const { params } = toRefs(props)

const { toast } = useToast()

const accessLevel = ref(params.value.data?.['Application Access'])
const accessLevelFlag = computed(() => accessLevel.value === 'All')
const hasAccessLevel = computed(() => ACCESS_LEVELS.includes(accessLevel.value))

const userName = computed(() =>
  params.value.data?.['Account Name'] ?? params.value.data?.Username ?? '',
)

const actionInProgress = ref(false)
const isOpen = ref(false)

// Path B: rule ID is embedded in the row data by GetCompanyUsers. The platform
// can also surface it via the grid context if a customer wants to inject it
// from elsewhere. Per-row beats per-grid because different rows can be on
// different rule versions during a rolling deploy.
const ruleId = computed(() =>
  params.value.data?.applicationAccessRuleId
    ?? params.value.context?.toggleApplicationAccessRuleId
    ?? null,
)

async function confirmToggle () {
  if (!ruleId.value) {
    toast({
      variant: 'danger',
      description: 'Missing rule ID for application access toggle. Please contact a system administrator.',
    })
    isOpen.value = false
    return
  }

  actionInProgress.value = true
  const newLevel = accessLevelFlag.value ? 'Personal' : 'All'

  try {
    const response = await triggerRule(ruleId.value, {
      rowId: params.value.data?.rowId ?? params.value.data?.id,
      this_user_name: userName.value,
      access_level: newLevel,
    })
    accessLevel.value = newLevel
    isOpen.value = false
    // Apply structured commands first (UPDATE_FIELD_VALUE etc.); fall back to
    // the optimistic update we just set if the rule didn't return commands.
    const handled = applyActionResponse(response, params.value)
    if (!handled) {
      params.value.node?.setDataValue?.('Application Access', newLevel)
    }
  } catch (ex) {
    console.error(ex)
    toast({
      variant: 'danger',
      description: ex?.message || 'Failed to change application access level',
    })
  } finally {
    actionInProgress.value = false
  }
}
</script>
